import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import toast from 'react-hot-toast';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const PendingDocuments = () => {
  const [pendingInsurance, setPendingInsurance] = useState([]);
  const [pendingLicenses, setPendingLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('insurance');

  useEffect(() => {
    loadPendingDocuments();
    
    // Auto-refresh every 5 minutes
    const intervalId = setInterval(() => {
      loadPendingDocuments();
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(intervalId);
  }, []);

  const loadPendingDocuments = async () => {
    try {
      setLoading(true);

      // Fetch pending insurance with mover details
      const { data: insurance, error: insError } = await supabase
        .from('mover_insurance')
        .select(`
          *,
          movers_data!inner(
            id,
            company_name,
            company_logo,
            email,
            phone,
            city,
            state
          )
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (insError) throw insError;

      // Fetch pending licenses with mover details
      const { data: licenses, error: licError } = await supabase
        .from('mover_licenses')
        .select(`
          *,
          movers_data!inner(
            id,
            company_name,
            company_logo,
            email,
            phone,
            city,
            state
          )
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (licError) throw licError;

      setPendingInsurance(insurance || []);
      setPendingLicenses(licenses || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleApproveInsurance = async (insuranceId) => {
    try {
      const { error } = await supabase
        .from('mover_insurance')
        .update({ status: 'approved' })
        .eq('id', insuranceId);

      if (error) throw error;

      // Remove from pending list
      setPendingInsurance(prev => prev.filter(ins => ins.id !== insuranceId));
      toast.success('Insurance approved successfully!');
    } catch (error) {
      toast.error('Failed to approve insurance');
    }
  };

  const handleRejectInsurance = async (insuranceId) => {
    try {
      const { error } = await supabase
        .from('mover_insurance')
        .update({ status: 'rejected' })
        .eq('id', insuranceId);

      if (error) throw error;

      // Remove from pending list
      setPendingInsurance(prev => prev.filter(ins => ins.id !== insuranceId));
      toast.success('Insurance rejected');
    } catch (error) {
      toast.error('Failed to reject insurance');
    }
  };

  const handleApproveLicense = async (licenseId) => {
    try {
      const { error } = await supabase
        .from('mover_licenses')
        .update({ status: 'approved' })
        .eq('id', licenseId);

      if (error) throw error;

      // Remove from pending list
      setPendingLicenses(prev => prev.filter(lic => lic.id !== licenseId));
      toast.success('License approved successfully!');
    } catch (error) {
      toast.error('Failed to approve license');
    }
  };

  const handleRejectLicense = async (licenseId) => {
    try {
      const { error } = await supabase
        .from('mover_licenses')
        .update({ status: 'rejected' })
        .eq('id', licenseId);

      if (error) throw error;

      // Remove from pending list
      setPendingLicenses(prev => prev.filter(lic => lic.id !== licenseId));
      toast.success('License rejected');
    } catch (error) {
      toast.error('Failed to reject license');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
        <h2 className="text-2xl font-bold text-white">Pending Documents Review</h2>
        <p className="text-blue-100 text-sm mt-1">Approve or reject mover insurance and licenses</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex">
          <button
            onClick={() => setActiveTab('insurance')}
            className={`px-6 py-4 font-medium transition-colors ${
              activeTab === 'insurance'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Insurance ({pendingInsurance.length})
          </button>
          <button
            onClick={() => setActiveTab('licenses')}
            className={`px-6 py-4 font-medium transition-colors ${
              activeTab === 'licenses'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Licenses ({pendingLicenses.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'insurance' ? (
          pendingInsurance.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-lg font-medium">No pending insurance to review</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingInsurance.map((insurance) => (
                <div
                  key={insurance.id}
                  className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center overflow-hidden">
                          {insurance.movers_data.company_logo ? (
                            <img 
                              src={insurance.movers_data.company_logo} 
                              alt={insurance.movers_data.company_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-xl font-bold text-blue-600">
                              {insurance.movers_data.company_name.charAt(0)}
                            </span>
                          )}
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">
                            {insurance.movers_data.company_name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {insurance.movers_data.city}, {insurance.movers_data.state}
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-500 font-medium mb-1">Insurance Type</p>
                          <p className="text-sm text-gray-900">{insurance.insurance_type || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium mb-1">Provider</p>
                          <p className="text-sm text-gray-900">{insurance.insurance_provider}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium mb-1">Policy Number</p>
                          <p className="text-sm text-gray-900">{insurance.policy_number}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium mb-1">Valid Period</p>
                          <p className="text-sm text-gray-900">
                            {insurance.valid_from && insurance.valid_to
                              ? `${new Date(insurance.valid_from).toLocaleDateString()} - ${new Date(insurance.valid_to).toLocaleDateString()}`
                              : 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      {insurance.coverage_details && (
                        <div className="mb-4">
                          <p className="text-xs text-gray-500 font-medium mb-1">Coverage Details</p>
                          <p className="text-sm text-gray-900">{insurance.coverage_details}</p>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {insurance.movers_data.email}
                        <span className="mx-2">•</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {insurance.movers_data.phone}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2 ml-4">
                      <button
                        onClick={() => handleApproveInsurance(insurance.id)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      >
                        ✓ Approve
                      </button>
                      <button
                        onClick={() => handleRejectInsurance(insurance.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                      >
                        ✗ Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          pendingLicenses.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-lg font-medium">No pending licenses to review</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingLicenses.map((license) => (
                <div
                  key={license.id}
                  className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center overflow-hidden">
                          {license.movers_data.company_logo ? (
                            <img 
                              src={license.movers_data.company_logo} 
                              alt={license.movers_data.company_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-xl font-bold text-purple-600">
                              {license.movers_data.company_name.charAt(0)}
                            </span>
                          )}
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">
                            {license.movers_data.company_name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {license.movers_data.city}, {license.movers_data.state}
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-500 font-medium mb-1">License Type</p>
                          <p className="text-sm text-gray-900">{license.license_type}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium mb-1">License Number</p>
                          <p className="text-sm text-gray-900">{license.license_number}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium mb-1">Issued By</p>
                          <p className="text-sm text-gray-900">{license.issued_by || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium mb-1">Valid Period</p>
                          <p className="text-sm text-gray-900">
                            {license.valid_from && license.valid_to
                              ? `${new Date(license.valid_from).toLocaleDateString()} - ${new Date(license.valid_to).toLocaleDateString()}`
                              : 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {license.movers_data.email}
                        <span className="mx-2">•</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {license.movers_data.phone}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2 ml-4">
                      <button
                        onClick={() => handleApproveLicense(license.id)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      >
                        ✓ Approve
                      </button>
                      <button
                        onClick={() => handleRejectLicense(license.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                      >
                        ✗ Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default PendingDocuments;
